import { useState, useEffect } from 'react'
import { Box, Flex, VStack, Grid, Input, Spinner } from '@chakra-ui/react'
import { LuArrowLeft as ArrowLeft, LuTruck as TruckIcon, LuSearch as Search, LuCheck as Check } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import { Modal, toaster, Text, useT } from '@minetrace/ui'
import { useFormColors } from '@minetrace/ui/forms'
import { themeColors } from '../../theme'
import { transportistasService } from '../../api/services'
import type { IncompleteTruckResponse, CarrierListItemResponse } from '../../api/models/response/transportista'

const VEHICLE_TYPES = [
  { value: 1, label: 'Camión' },
  { value: 2, label: 'Camioneta' },
  { value: 3, label: 'Tráiler' },
]
const FUEL_TYPES = [
  { value: 1, label: 'Diésel' },
  { value: 2, label: 'Gasolina' },
  { value: 3, label: 'GNV' },
  { value: 4, label: 'GLP' },
  { value: 5, label: 'Eléctrico' },
  { value: 6, label: 'Híbrido' },
]

interface RegForm {
  marca: string
  modelo: string
  anio?: number
  capacidadToneladas?: number
  numeroConstanciaInscripcion: string
  vehicleTypeId?: number
  fuelTypeId?: number
  performanceKmPerGal?: number
}

const EMPTY_FORM: RegForm = { marca: '', modelo: '', numeroConstanciaInscripcion: '' }

export default function RegularizarVehiculosPage() {
  const colors = themeColors
  const c = useFormColors()
  const { t } = useT()
  const navigate = useNavigate()

  const [trucks, setTrucks] = useState<IncompleteTruckResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)

  // Modal de regularización
  const [target, setTarget] = useState<IncompleteTruckResponse | null>(null)
  const [form, setForm] = useState<RegForm>(EMPTY_FORM)
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null)
  const [carrierSearch, setCarrierSearch] = useState('')
  const [carrierResults, setCarrierResults] = useState<CarrierListItemResponse[]>([])
  const [searching, setSearching] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let active = true
    setLoading(true)
    transportistasService
      .listIncompleteTrucks()
      .then((d) => { if (active) setTrucks(d) })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [reloadKey])

  const inputStyles = {
    bg: c.bgInput,
    border: 'none',
    borderRadius: 'xl',
    color: c.text,
    _placeholder: { color: c.textMuted },
    _focus: { outline: 'none', ring: '1px', ringColor: c.accent },
  } as const
  const selectStyles: React.CSSProperties = {
    width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.75rem', border: 'none',
    fontSize: '0.875rem', background: c.bgInput, color: c.text, outline: 'none',
  }

  const openModal = (truck: IncompleteTruckResponse) => {
    setTarget(truck)
    setForm({ ...EMPTY_FORM, vehicleTypeId: truck.vehicleTypeId ?? undefined })
    setPicked(null)
    setCarrierSearch('')
    setCarrierResults([])
  }
  const closeModal = () => setTarget(null)

  const searchCarriers = async () => {
    setSearching(true)
    try {
      const res = await transportistasService.getAll({ search: carrierSearch.trim() || undefined, pageSize: 8 })
      setCarrierResults(res.items)
    } catch {
      setCarrierResults([])
    } finally {
      setSearching(false)
    }
  }

  const handleRegularize = async () => {
    if (!target || !picked) {
      toaster.error({ description: t('transportistas.regularizar.pickRequired') })
      return
    }
    setSaving(true)
    try {
      await transportistasService.regularizeTruck(target.id, {
        companyId: picked.id,
        marca: form.marca || undefined,
        modelo: form.modelo || undefined,
        anio: form.anio,
        capacidadToneladas: form.capacidadToneladas,
        numeroConstanciaInscripcion: form.numeroConstanciaInscripcion || undefined,
        vehicleTypeId: form.vehicleTypeId,
        fuelTypeId: form.fuelTypeId,
        performanceKmPerGal: form.performanceKmPerGal,
      })
      toaster.success({ description: t('transportistas.regularizar.done') })
      closeModal()
      setReloadKey((k) => k + 1)
    } catch {
      toaster.error({ description: t('transportistas.regularizar.error') })
    } finally {
      setSaving(false)
    }
  }

  return (
    <VStack gap={6} alignItems="stretch">
      <Box>
        <Box as="button" display="flex" alignItems="center" gap={1.5} color={colors.textMuted} fontSize="sm" mb={1} cursor="pointer" _hover={{ color: colors.text }} onClick={() => navigate('/app/transportistas')}>
          <ArrowLeft size={16} />
          {t('transportistas.regularizar.back')}
        </Box>
        <Text fontSize="2xl" fontWeight="800" color={colors.text}>{t('transportistas.regularizar.title')}</Text>
        <Text fontSize="sm" color={colors.textMuted} mt={0.5}>{t('transportistas.regularizar.subtitle')}</Text>
      </Box>

      {loading ? (
        <Box p={8} textAlign="center"><Spinner /></Box>
      ) : trucks.length === 0 ? (
        <Box p={8} textAlign="center" bg={colors.bgCard} borderRadius="xl">
          <Text fontSize="sm" color={colors.textMuted}>{t('transportistas.regularizar.empty')}</Text>
        </Box>
      ) : (
        <VStack gap={3} alignItems="stretch">
          {trucks.map((truck) => (
            <Flex key={truck.id} p={4} bg={colors.bgPanel} border="1px solid" borderColor={colors.border} borderRadius="xl" alignItems="center" justifyContent="space-between" gap={3}>
              <Flex alignItems="center" gap={3}>
                <Box width="40px" height="40px" borderRadius="lg" bg={colors.bgHover} display="flex" alignItems="center" justifyContent="center">
                  <TruckIcon size={20} color={colors.textMuted} />
                </Box>
                <Box>
                  <Text fontFamily="monospace" fontSize="sm" fontWeight="700" color={colors.text}>{truck.placa}</Text>
                  <Text fontSize="xs" color={colors.textMuted}>
                    {t('transportistas.regularizar.detectedOn', { date: new Date(truck.createdAt).toLocaleDateString() })}
                  </Text>
                </Box>
              </Flex>
              <Box as="button" px={4} py={2} borderRadius="lg" bg={colors.bgHover} color={colors.text} fontWeight="600" fontSize="sm" cursor="pointer" _hover={{ bg: colors.border }} onClick={() => openModal(truck)}>
                {t('transportistas.regularizar.action')}
              </Box>
            </Flex>
          ))}
        </VStack>
      )}

      <Modal
        open={target !== null}
        onClose={closeModal}
        title={t('transportistas.regularizar.modalTitle', { placa: target?.placa ?? '' })}
        size="lg"
        footer={
          <Flex gap={3} justifyContent="flex-end">
            <Box as="button" px={4} py={2} borderRadius="lg" border="1px solid" borderColor={c.border} color={c.text} fontWeight="600" fontSize="sm" cursor="pointer" _hover={{ bg: c.bgHover }} onClick={closeModal}>
              {t('common.cancel')}
            </Box>
            <Box as="button" px={4} py={2} borderRadius="lg" bg={c.accent} color="white" fontWeight="600" fontSize="sm" cursor="pointer" opacity={picked && !saving ? 1 : 0.5} _hover={{ opacity: picked && !saving ? 0.9 : 0.5 }} onClick={handleRegularize}>
              {saving ? t('common.saving') : t('transportistas.regularizar.confirm')}
            </Box>
          </Flex>
        }
      >
        {/* Búsqueda de transportista */}
        <Text fontSize="sm" fontWeight="600" color={c.text} mb={2}>{t('transportistas.regularizar.assignCarrier')}</Text>
        {picked ? (
          <Flex justifyContent="space-between" alignItems="center" bg={`${c.accent}10`} border="1px solid" borderColor={`${c.accent}30`} borderRadius="xl" p={3} mb={4}>
            <Text fontSize="sm" fontWeight="600" color={c.text}>{picked.name}</Text>
            <Box as="button" fontSize="xs" color={c.accent} cursor="pointer" onClick={() => setPicked(null)}>{t('transportistas.regularizar.change')}</Box>
          </Flex>
        ) : (
          <Box mb={4}>
            <Flex gap={2}>
              <Input
                value={carrierSearch}
                onChange={(e) => setCarrierSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchCarriers() } }}
                placeholder={t('transportistas.regularizar.searchPlaceholder')}
                {...inputStyles}
              />
              <Box as="button" display="flex" alignItems="center" justifyContent="center" px={4} bg={c.accentGradient} color="white" borderRadius="xl" cursor="pointer" flexShrink={0} minW="48px" onClick={searchCarriers}>
                {searching ? <Spinner size="sm" color="white" /> : <Search size={18} />}
              </Box>
            </Flex>
            {carrierResults.length > 0 && (
              <VStack gap={1} alignItems="stretch" mt={2} maxH="200px" overflowY="auto">
                {carrierResults.map((carrier) => (
                  <Flex key={carrier.id} p={2.5} bg={c.bgHover} borderRadius="lg" alignItems="center" justifyContent="space-between" cursor="pointer" _hover={{ bg: c.bgInput }} onClick={() => setPicked({ id: carrier.id, name: carrier.legalName })}>
                    <Box>
                      <Text fontSize="sm" color={c.text}>{carrier.legalName}</Text>
                      <Text fontSize="xs" color={c.textMuted}>{carrier.ruc}</Text>
                    </Box>
                    <Check size={16} color={c.textMuted} />
                  </Flex>
                ))}
              </VStack>
            )}
          </Box>
        )}

        {/* Datos del vehículo */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
          <Box>
            <Text fontSize="xs" fontWeight="500" color={c.textSecondary} mb={1}>{t('transportistas.nuevo.fields.vehicleType')}</Text>
            <select value={form.vehicleTypeId ?? ''} onChange={(e) => setForm((p) => ({ ...p, vehicleTypeId: e.target.value ? Number(e.target.value) : undefined }))} style={selectStyles}>
              <option value="">{t('transportistas.nuevo.fields.selectPlaceholder')}</option>
              {VEHICLE_TYPES.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="500" color={c.textSecondary} mb={1}>{t('transportistas.nuevo.fields.fuelType')}</Text>
            <select value={form.fuelTypeId ?? ''} onChange={(e) => setForm((p) => ({ ...p, fuelTypeId: e.target.value ? Number(e.target.value) : undefined }))} style={selectStyles}>
              <option value="">{t('transportistas.nuevo.fields.selectPlaceholder')}</option>
              {FUEL_TYPES.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="500" color={c.textSecondary} mb={1}>{t('transportistas.nuevo.fields.marca')}</Text>
            <Input value={form.marca} onChange={(e) => setForm((p) => ({ ...p, marca: e.target.value }))} {...inputStyles} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="500" color={c.textSecondary} mb={1}>{t('transportistas.nuevo.fields.modelo')}</Text>
            <Input value={form.modelo} onChange={(e) => setForm((p) => ({ ...p, modelo: e.target.value }))} {...inputStyles} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="500" color={c.textSecondary} mb={1}>{t('transportistas.nuevo.fields.anio')}</Text>
            <Input type="number" value={form.anio ?? ''} onChange={(e) => setForm((p) => ({ ...p, anio: e.target.value ? parseInt(e.target.value) : undefined }))} {...inputStyles} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="500" color={c.textSecondary} mb={1}>{t('transportistas.nuevo.fields.capacidad')}</Text>
            <Input type="number" value={form.capacidadToneladas ?? ''} onChange={(e) => setForm((p) => ({ ...p, capacidadToneladas: e.target.value ? parseFloat(e.target.value) : undefined }))} {...inputStyles} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="500" color={c.textSecondary} mb={1}>{t('transportistas.nuevo.fields.performance')}</Text>
            <Input type="number" value={form.performanceKmPerGal ?? ''} onChange={(e) => setForm((p) => ({ ...p, performanceKmPerGal: e.target.value ? parseFloat(e.target.value) : undefined }))} {...inputStyles} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="500" color={c.textSecondary} mb={1}>{t('transportistas.nuevo.fields.constancia')}</Text>
            <Input value={form.numeroConstanciaInscripcion} onChange={(e) => setForm((p) => ({ ...p, numeroConstanciaInscripcion: e.target.value }))} {...inputStyles} />
          </Box>
        </Grid>
      </Modal>
    </VStack>
  )
}
